import React, { useState } from "react";
import {
  TrendingUp,
  Clock,
  Megaphone,
  SlidersHorizontal,
  ShieldCheck,
  Layers,
  Building2,
  Wallet,
} from "lucide-react";
import CtaSection, { CtaButton } from "../components/common/CtaSection";
import LeadCaptureModal from "../components/common/LeadCaptureModal";
import { LEAD_CHANNELS } from "../constants/data";
// import whygb from "../assets/logos/whygb.png";
import heroImg2 from "../assets/Image4.png";

const GOLD = "#50C878";
const NAVY = "#013220";

// ─── WHY DEVELOPERS CHOOSE US ─────────────────────────────────
// Developer-facing value proposition. Built from the GroupBuy
// "Funding Through Sales" developer copy — positions GroupBuy as a
// capital-generation strategy rather than a marketing channel.
// ─────────────────────────────────────────────────────────────

const UNLOCK_POINTS = [
  "Generate large-scale bookings in a single closure event",
  "Maintain complete pricing control and negotiation authority",
  "Reduce marketing dependency and advertising costs",
  "Minimise unsold inventory holding time",
  "Accelerate project cash flow",
];

const ADVANTAGES = [
  {
    icon: TrendingUp,
    title: "Faster Capital Generation",
    body: "Convert buyer demand into project funding through a structured group closure.",
  },
  {
    icon: Clock,
    title: "Reduced Sales Cycle",
    body: "Sell multiple units at once instead of waiting for individual buyers over time.",
  },
  {
    icon: Megaphone,
    title: "Lower Marketing Expenses",
    body: "Minimise advertising and promotional spending required for retail sales.",
  },
  {
    icon: SlidersHorizontal,
    title: "Controlled Pricing Strategy",
    body: "Developers maintain full control over final pricing and terms.",
  },
  {
    icon: ShieldCheck,
    title: "Structured & Confidential Process",
    body: "All buyer groups are organised professionally with verified intent and financial readiness.",
  },
  {
    icon: Layers,
    title: "Scalable for Any Project Size",
    body: "From boutique developments to large-scale residential communities, our model adapts to your project.",
  },
];

export default function WhyDeveloperChooseUs() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div style={{ background: "#F2F8F3", color: NAVY, minHeight: "100vh", paddingTop: "68px" }}>
      {/* Hero */}
      <div
        style={{
          position: "relative",
          padding: 0,
          textAlign: "left",
          backgroundImage: `url(${heroImg2})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          borderBottom: "1px solid rgba(80,200,120,0.2)",
          overflow: "hidden",
          height: "80vh",
          minHeight: "500px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-start",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(15,30,50,0.85) 0%, rgba(15,30,50,0.6) 40%, rgba(15,30,50,0.2) 70%, rgba(15,30,50,0) 100%)",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "60px",
            left: "60px",
            zIndex: 2,
            maxWidth: "680px",
            padding: "0 24px",
          }}
        >
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.6rem",
              color: GOLD,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              textShadow: "0 1px 4px rgba(0,0,0,0.45)",
              display: "block",
              marginBottom: "6px",
            }}
          >
            For Developers
          </span>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              fontWeight: 600,
              margin: "0 0 12px",
              color: "#FFFFFF",
              textShadow: "0 2px 14px rgba(0,0,0,0.5)",
              lineHeight: 1.2,
            }}
          >
            Funding Through Sales
          </h1>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "clamp(0.95rem, 1.3vw, 1.1rem)",
              color: "rgba(255,255,255,0.92)",
              lineHeight: 1.7,
              maxWidth: "560px",
              margin: "0 0 24px",
              textShadow: "0 1px 8px rgba(0,0,0,0.5)",
            }}
          >
            Your project already has value. We simply accelerate the capital flow — unlocking committed
            buyer funds through a single coordinated group closure.
          </p>
          <CtaButton label="Let's Talk GroupBuy" onClick={() => setShowModal(true)} />
        </div>
      </div>

      {/* Funding Through Sales intro */}
      <section style={{ maxWidth: "900px", margin: "0 auto", padding: "60px 24px 40px" }}>
        <div
          style={{
            background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
            border: "1px solid rgba(80,200,120,0.18)",
            borderRadius: "14px",
            padding: "40px 44px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <Wallet size={28} color={GOLD} />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 600, margin: 0 }}>
              A Structured Sales Model Built for Capital
            </h2>
          </div>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.02rem", color: "rgba(1,50,32,0.78)", lineHeight: 1.9, margin: "0 0 18px" }}>
            GroupBuy Flats is a structured real-estate sales model designed to help developers unlock large
            volumes of committed buyer funds through a single coordinated group closure. Unlike traditional
            marketing-driven sales, our approach focuses on fund mobilisation through organised buyer groups,
            allowing developers to raise capital faster while maintaining full control over pricing, timelines
            and sales processes.
          </p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.02rem", color: "rgba(1,50,32,0.78)", lineHeight: 1.9, margin: "0 0 18px" }}>
            We specialise in bringing together serious, qualified buyers with verified budgets and purchase
            intent, enabling developers to convert demand into capital through one efficient transaction cycle.
          </p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.02rem", color: "rgba(1,50,32,0.78)", lineHeight: 1.9, margin: 0 }}>
            Through our closed-group sales strategy, developers can significantly reduce sales timelines,
            marketing expenses and holding costs while achieving faster project funding.
          </p>
        </div>
      </section>

      {/* Unlock Bulk Sales */}
      <section style={{ maxWidth: "1100px", margin: "0 auto", padding: "20px 24px 50px" }}>
        <div
          style={{
            background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
            border: "1px solid rgba(80,200,120,0.18)",
            borderRadius: "14px",
            padding: "40px 44px",
          }}
        >
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 600, margin: "0 0 6px" }}>
            Unlock Bulk Sales
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 18px", fontWeight: 600 }}>
            Close Faster. Stay in Control.
          </p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.02rem", color: "rgba(1,50,32,0.78)", lineHeight: 1.9, margin: "0 0 18px" }}>
            Real estate developers often spend months or years selling units individually through marketing
            campaigns, channel partners, and retail buyers. This process requires significant time, advertising
            budgets, operational cost and effort. GroupBuy Flats simplifies this by creating a single organised
            buyer group capable of closing multiple units at once.
          </p>

          <div
            style={{
              background: "rgba(80,200,120,0.06)",
              borderRadius: "10px",
              padding: "24px 28px",
              borderLeft: "3px solid #50C878",
              marginBottom: "20px",
            }}
          >
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 600, margin: "0 0 14px" }}>
              Our model allows developers to:
            </h3>
            <div style={{ display: "grid", gap: "12px" }}>
              {UNLOCK_POINTS.map((point) => (
                <div key={point} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: GOLD,
                      marginTop: "8px",
                      flexShrink: 0,
                    }}
                  />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1rem", color: "rgba(1,50,32,0.8)", lineHeight: 1.7, margin: 0 }}>
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.02rem", color: "rgba(1,50,32,0.78)", lineHeight: 1.9, margin: 0 }}>
            Instead of selling unit-by-unit over months, developers can unlock bulk demand through one
            coordinated group transaction.
          </p>
        </div>
      </section>

      {/* Why Developers Choose GroupBuy — advantages grid */}
      <section
        style={{
          background: "#F7FBF8",
          borderTop: "1px solid rgba(80,200,120,0.12)",
          borderBottom: "1px solid rgba(80,200,120,0.12)",
          padding: "60px 24px",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 600, margin: "0 0 12px" }}>
              Why Developers Choose GroupBuy
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1rem", color: "rgba(1,50,32,0.7)", lineHeight: 1.8, maxWidth: "720px", margin: "0 auto" }}>
              Traditional real estate sales rely heavily on marketing campaigns, broker networks, and slow
              retail absorption — increasing holding costs and delaying capital recovery. GroupBuy Flats
              transforms the sales process into a Capital Generation Strategy.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "22px",
              marginTop: "36px",
            }}
          >
            {ADVANTAGES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                style={{
                  background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
                  border: "1px solid rgba(80,200,120,0.18)",
                  borderRadius: "14px",
                  padding: "28px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "10px",
                    background: "rgba(80,200,120,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Icon size={24} color={GOLD} />
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 600, margin: "0 0 10px" }}>
                  {title}
                </h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", color: "rgba(1,50,32,0.72)", lineHeight: 1.7, margin: 0 }}>
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy banner */}
      <section style={{ maxWidth: "900px", margin: "0 auto", padding: "60px 24px" }}>
        <div
          style={{
            background: "linear-gradient(135deg, #013220 0%, #024A30 100%)",
            borderRadius: "16px",
            padding: "48px 44px",
            textAlign: "center",
          }}
        >
          <Building2 size={32} color={GOLD} style={{ marginBottom: "16px" }} />
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.05rem", color: "rgba(255,255,255,0.82)", lineHeight: 1.8, margin: "0 0 24px" }}>
            GroupBuy is not just about selling. It's about converting inventory into working capital.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "28px" }}>
            {["One Group.", "One Closure.", "One Powerful Fund."].map((line) => (
              <span
                key={line}
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.6rem",
                  fontWeight: 600,
                  color: "#FFFFFF",
                }}
              >
                {line}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        eyebrow="For developers"
        title="Let's structure your group sales solution"
        subtitle="If you're a developer looking to unlock faster capital and accelerate your project sales cycle, GroupBuy Flats can build a customised group sales solution for your project."
        primaryLabel="Partner With Us"
        onPrimaryClick={() => setShowModal(true)}
      />

      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        channel={LEAD_CHANNELS.WEB}
        prefillMessage="Developer enquiry from the Why Developers Choose Us page"
      />
    </div>
  );
}
